import React from "react";

/**
 * Canonical BENO brand mark.
 *
 * Renders the official logo asset (`/brand/beno-logo.jpg`) — the master file
 * supplied by the brand owner. Never re-creates, redraws, recolours, or
 * stretches the artwork; only scales it via `size` and the `aspect-square`
 * container. Use this anywhere the BENO logo is needed (headers, splashes,
 * admin login, exported assets).
 *
 * Usage:
 *   <BenoLogo size={36} />            // header / sidebar
 *   <BenoLogo size={120} alt="..."/>  // splash / login hero
 *
 * Props:
 *   size   — px height of the rendered mark (defaults to 36)
 *   alt    — accessible label (defaults to "BENO")
 *   className — extra wrapper classes
 */
export default function BenoLogo({ size = 36, alt = "BENO", className = "", invert = false }) {
  // The supplied master asset is white-on-black. We use `mix-blend-mode: screen`
  // (a non-destructive CSS compositor) so the black background drops away over
  // the dark BENO page while the white logo pixels remain crisp. The asset
  // file itself is never modified — proportions, colors, and bytes are intact.
  // For light backgrounds, pass `invert` to fall back to a neutral framed tile.
  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 ${className}`}
      style={{ width: size, height: size }}
      data-testid="beno-logo"
    >
      <img
        src="/brand/beno-logo.jpg"
        alt={alt}
        width={size}
        height={size}
        loading="eager"
        decoding="async"
        draggable="false"
        className="block w-full h-full object-contain select-none"
        style={{
          mixBlendMode: invert ? "normal" : "screen",
          imageRendering: "auto",
        }}
      />
    </span>
  );
}
